/* eslint-disable max-lines */
import { gql } from 'graphql-tag';
import { TypedDocumentNode } from '@apollo/client';

import {
  GetFishingWaterForCard,
  GetFishingWaterForCardVariables,
  GetFishingWaterMetadata,
  GetFishingWaterMetadataVariables,
  GetFishingWaterReviews,
  GetFishingWaterReviewsVariables,
  GetFishingWaterSummary,
  GetFishingWaterSummaryVariables,
  GetNearbyFishingWaters,
  GetNearbyFishingWatersVariables,
} from '@generated/graphql';
import { CatchFieldsForPostsViewer } from '@graphql/fragments/rutilus/CatchFieldsForPostsViewer';
import { CatchFragments } from '@graphql/fragments/rutilus/CatchType';
import { FishingWaterReviewFragments } from '@graphql/fragments/rutilus/FishingWaterReviewType';
import { FishingWaterFragments } from '@graphql/fragments/rutilus/FishingWaterType';
import { ImageFragments } from '@graphql/fragments/rutilus/ImageType';
import { PlaceFragments } from '@graphql/fragments/rutilus/PlaceType';
import { PostFragments } from '@graphql/fragments/rutilus/PostType';
import { SpeciesFragments } from '@graphql/fragments/rutilus/SpeciesTypes';
import { UserFragments } from '@graphql/fragments/rutilus/UserType';

/* Directory page listing */
export const getDirectoryFishingWaters = gql`
  query GetDirectoryFishingWaters($first: Int!, $after: String, $startsWith: String) {
    entry: fishingWaters(
      first: $first
      after: $after
      filters: { startsWith: $startsWith, orderedByName: true }
    ) {
      edges {
        cursor
        node {
          ...FishingWaterId
          displayName
          region {
            name
          }
          country {
            countryName
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
  ${FishingWaterFragments.id}
`;

/* Fishing water in a map drawer */
export const getFishingWaterSummary: TypedDocumentNode<
  GetFishingWaterSummary,
  GetFishingWaterSummaryVariables
> = gql`
  query GetFishingWaterSummary($externalId: String, $first: Int, $after: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterSummaryFields
    }
  }
  ${FishingWaterFragments.summaryFields}
  ${CatchFieldsForPostsViewer}
`;

/* Fishing water page, review section */
export const getFishingWaterReviews: TypedDocumentNode<
  GetFishingWaterReviews,
  GetFishingWaterReviewsVariables
> = gql`
  query GetFishingWaterReviews(
    $first: Int
    $after: String
    $externalId: String
    $orderByBestReviews: Boolean
    $orderByRecent: Boolean
  ) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      oneStarReviews: reviews(filters: { havingOneStarRating: true }) {
        totalCount
      }
      twoStarReviews: reviews(filters: { havingTwoStarRating: true }) {
        totalCount
      }
      threeStarReviews: reviews(filters: { havingThreeStarRating: true }) {
        totalCount
      }
      fourStarReviews: reviews(filters: { havingFourStarRating: true }) {
        totalCount
      }
      fiveStarReviews: reviews(filters: { havingFiveStarRating: true }) {
        totalCount
      }
      reviews(
        first: $first
        after: $after
        filters: {
          havingTextOrTitle: true
          orderByBestReviews: $orderByBestReviews
          orderByRecent: $orderByRecent
        }
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...FishingWaterReviewId
            externalId
            rating
            createdAt
            author {
              ...UserId
              nickname
              lastName
              firstName
            }
            post {
              ...PostId
              helpfulDownvotesCount
              helpfulUpvotesCount
              currentUserFoundHelpful
              titleString
              text {
                text
              }
            }
          }
        }
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterReviewFragments.id}
  ${PostFragments.id}
  ${UserFragments.id}
`;

/* Fishing water page, topspecies section */
export const getFishingWaterTopSpecies = gql`
  query GetFishingWaterTopSpecies($externalId: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      topSpeciesSummary(first: 3) {
        ...FishingWaterTopSpeciesSummaryFields
      }
    }
  }
  ${FishingWaterFragments.id}
  ${SpeciesFragments.fishingWaterTopSpeciesSummaryFields}
`;

/* Fishing water page, nearby places section */
export const getFishingWaterNearestPlace = gql`
  query GetFishingWaterNearestPlace($externalId: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      nearestPlace {
        ...NearestPlaceFields
      }
    }
  }
  ${FishingWaterFragments.id}
  ${PlaceFragments.nearestPlaceFields}
`;

/* Fishing water page, nearby waters  */
export const getFishingWaterNearbyWaters = gql`
  query GetFishingWaterNearbyWaters($externalId: String, $recentCatchLimitDays: Int) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      suggestedWaters {
        ...SuggestedWaterFields
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.suggestedWaterFields}
`;

/* Fishing water page, metadata generation for SEO */
export const getFishingWaterSEOMetadata = gql`
  query GetFishingWaterSEOMetadata($externalId: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      ...FishingWaterLocationFields
      displayName
      nearestPlace {
        ...PlaceId
        name
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
  ${PlaceFragments.id}
`;

/* Fishing water page, main query */
export const getFishingWater = gql`
  query GetFishingWater($externalId: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterDetailFields
    }
  }
  fragment FishingWaterDetailCatchNodeFields on Catch {
    ...CatchId
    post {
      ...PostId
      catch {
        ...CatchId
        ...CatchFishingWaterName
        ...CatchSpeciesName
        caughtAtGmt
        weight
        length
      }
      createdAt
      displayProductUnits {
        totalCount
      }
      images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
        totalCount
        edges {
          node {
            ...ImageFields
          }
        }
      }
      user {
        ...UserId
        nickname
      }
    }
  }
  ${CatchFragments.id}
  ${CatchFragments.fishingWaterName}
  ${CatchFragments.speciesName}
  ${FishingWaterFragments.detailFields}
  ${FishingWaterReviewFragments.allReviewsFields}
  ${FishingWaterReviewFragments.amenityNodeFields}
  ${ImageFragments.imageFields}
  ${PostFragments.id}
  ${UserFragments.id}
  ${UserFragments.fishingWaterDetailTopAnglersFields}
`;

export const getFishingWaterForSuggestChanges: TypedDocumentNode<
  GetFishingWaterForCard,
  GetFishingWaterForCardVariables
> = gql`
  query GetFishingWaterForSuggestChanges($externalId: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      displayName
      latitude
      longitude
      url
      metadata {
        ...FishingWaterDetailMetadataFields
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.detailMetadataFields}
`;

/* Used in making a post when you add a location */
export const getFishingWaterForCard: TypedDocumentNode<
  GetFishingWaterForCard,
  GetFishingWaterForCardVariables
> = gql`
  query GetFishingWaterForCard($externalId: String) {
    entry: fishingWater(externalId: $externalId) {
      ...FishingWaterId
      ...FishingWaterLocationFields
      displayName
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
`;

export const getFishingWaterLocation = gql`
  query GetFishingWaterLocation($externalId: String) {
    entry: fishingWater(externalId: $externalId) {
      ...FishingWaterId
      latitude
      longitude
    }
  }
  ${FishingWaterFragments.id}
`;

/* Used for redirecting */
export const getFishingWaterName = gql`
  query GetFishingWaterName($externalId: String) {
    entry: fishingWater(externalId: $externalId) {
      ...FishingWaterId
      displayName
    }
  }
  ${FishingWaterFragments.id}
`;

export const getFishingWaterNames = gql`
  query GetFishingWaterNames($externalIds: [String!], $first: Int, $filters: FishingWaterFilters) {
    entries: fishingWaters(first: $first, externalIds: $externalIds, filters: $filters) {
      edges {
        node {
          ...FishingWaterId
          displayName
        }
      }
    }
  }
  ${FishingWaterFragments.id}
`;

export const getNearbyFishingWaters: TypedDocumentNode<
  GetNearbyFishingWaters,
  GetNearbyFishingWatersVariables
> = gql`
  query GetNearbyFishingWaters($first: Int, $boundingBox: BoundingBoxInputObject) {
    entry: fishingWaters(
      first: $first
      filters: { orderByCatchesCount: true, withCatches: true, publicOnly: true }
      boundingBox: $boundingBox
    ) {
      edges {
        node {
          ...FishingWaterId
          ...FishingWaterLocationFields
          catchesCount
          displayName
          allReviews: reviews {
            totalCount
            totalAverage(attribute: RATING)
          }
          recentCatches: catches(first: 4, filters: { withPhotos: true, orderByRecent: true }) {
            edges {
              node {
                ...CatchId
                post {
                  ...PostId
                  images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
                    totalCount
                    edges {
                      node {
                        ...ImageFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${PostFragments.id}
  ${ImageFragments.imageFields}
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
`;

/* Used in suggest water page */
export const getFishingWaterMetadata: TypedDocumentNode<
  GetFishingWaterMetadata,
  GetFishingWaterMetadataVariables
> = gql`
  query GetFishingWaterMetadata($cursor: String) {
    fishingWaterMetadata(first: 100, after: $cursor) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        node {
          ...FishingWaterMetadataNodeFields
        }
      }
    }
  }
  ${FishingWaterFragments.metadataNodeFields}
`;

/* Used for top waters info */
export const getTopWatersInfo = gql`
  query GetTopWatersInfo($externalIds: [String!], $first: Int) {
    fishingWaters(
      first: $first
      externalIds: $externalIds
      filters: { orderByCatchesCount: true }
    ) {
      edges {
        node {
          ...TopWatersInfoNodeFields
        }
      }
      totalCount
    }
  }
  ${FishingWaterFragments.topWatersInfoNodeFields}
`;
